import { Fragment } from 'react';
import Link from 'next/link';
import { FaLock } from 'react-icons/fa';

import { PRICING_PAGE } from 'constants/pages';
import { ENABLE_OPTION_FLOW_READ_MODE } from 'utils/shared/trades/optionFlowRead';
import { useCommandCenter, getFlowFilterFunction } from 'hooks/useCommandCenter';
import { TickerAggregatedDetails } from 'components/OptionAggregatedTable/props';
import SliderFilter from './Slider';
import {
  OPTION_TYPE,
  TRADE_TYPE,
  PRICE_OPTIONS,
  FLOW_SIDE_OPTIONS,
  FLOW_METRIC_MODE_OPTIONS,
  READ_LABEL_OPTIONS,
  TICKER_TYPE_OPTIONS,
  OTM_OPTIONS,
  TICKER_FLOW_OPTIONS,
  MOMENTUM_OPTIONS,
  SIZE_OPTIONS,
  PREMIUM_OPTIONS,
  VOLATILITY_OPTIONS,
  OPEN_INTEREST_OPTIONS,
  SECTOR_OPTIONS,
  TIME_OPTIONS,
  MARKET_CAP_OPTIONS,
} from './filterOptions';
import {
  SliderDirection,
  EQUITY_FILTER,
  FlowReadLabelSelection,
  getEffectiveFlowOptionType,
  getDefaultFlowSideBasisSelection,
  parseFlowMetricMode,
  normalizeFlowReadLabelSelection,
  normalizeFlowSideBasisSelection,
} from 'utils/shared/trades/presets';
import type { FlowMetricMode, FlowSideBasis } from 'utils/shared/trades/presets';
import { FilterButtonGroup } from './FilterButtonGroup';
import { FilterButtonGroupWithLabel } from './LabelFilterButtonGroup';
import { CommandRow, OutlineButtonWhite } from './styles';
import { Trade } from 'utils/shared/trades/types';
import ExpirationDateDropdown from './ExpirationDateDropdown';
import { trackEvent } from 'services/client/analytics';
import {
  FLOW_METRIC_MODE_CHANGED,
  FLOW_SIDE_BASIS_CHANGED,
} from 'services/client/analytics/events';

export type OptionFlowFilterSectionKey =
  | 'tickerType'
  | 'sector'
  | 'marketCap'
  | 'momentum'
  | 'flowDirection';

interface StateProps {
  state: ReturnType<typeof useCommandCenter>['state'];
  dispatch: ReturnType<typeof useCommandCenter>['dispatch'];
  ActionTypes: ReturnType<typeof useCommandCenter>['ActionTypes'];
}

interface RowProps {
  enabled?: boolean;
  allOptionFlowTrades?: Trade[];
  tradesByTickerMap?: Record<string, TickerAggregatedDetails>;
  hiddenSections?: OptionFlowFilterSectionKey[];
  // Free / anonymous viewers: everything except the aggressive ask-side pair (Above Ask,
  // Ask) is locked behind an upgrade CTA.
  arePremiumSidesLocked?: boolean;
}

// The only trade sides a non-paid viewer may select. Everything else — Mid, Bid,
// Below Bid, and the All shortcut (which spans them) — is Pro.
const FREE_FLOW_SIDE_VALUES = ['aboveAsk', 'ask'];

const Row: React.FC<React.PropsWithChildren<RowProps>> = ({ children, enabled }) => (
  <CommandRow
    className={`py-3 text-sm last:mb-0 ${
      enabled
        ? 'block'
        : 'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-0'
    }`}
  >
    {children}
  </CommandRow>
);

const RowTitle: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <div className="text-base font-semibold sm:text-lg">{children}</div>
);

const RowDescription: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <div className="mt-1 text-xs font-light text-gray-400 sm:mt-0 sm:pr-16">{children}</div>
);

export function OptionFlowFilters({
  enabled = false,
  state,
  dispatch,
  ActionTypes,
  allOptionFlowTrades = [],
  tradesByTickerMap = {},
  hiddenSections = [],
  arePremiumSidesLocked = false,
}: RowProps & StateProps) {
  const isHidden = (key: OptionFlowFilterSectionKey) => hiddenSections.includes(key);
  const isFlowSideLocked = (value?: string) =>
    arePremiumSidesLocked && !FREE_FLOW_SIDE_VALUES.includes(value ?? '');
  const activeFlowSides = normalizeFlowSideBasisSelection(state.flowSideBasis);
  const activeReadLabels = normalizeFlowReadLabelSelection(state.readLabels);
  const activeFlowMetricMode = parseFlowMetricMode(state.flowMetricMode) || 'total';
  const activeFlowMetricModeLabel =
    FLOW_METRIC_MODE_OPTIONS.find((option) => option.value === activeFlowMetricMode)?.label ||
    'Total';
  const activeFlowSideLabels = FLOW_SIDE_OPTIONS.filter((option) =>
    activeFlowSides.includes(option.value as FlowSideBasis),
  ).map((option) => option.label);
  const activeOptionType = getEffectiveFlowOptionType(state.optionType, activeFlowSides);
  // Trade Side now owns the full ask↔bid spectrum (including Above Ask / Below Bid),
  // so the Flow Type chips are a stable, static list that never reflows on selection.
  const tradesForExpirationDropdown = allOptionFlowTrades.filter(
    getFlowFilterFunction(state, tradesByTickerMap, Date.now(), {
      skipExpirationFilters: true,
      skipTickerTypeFilter: true,
    }),
  );

  const getExpirationDatesForBucket = (bucketType: 'ultrashort' | 'short' | 'medium' | 'long') => {
    const matchingDates: string[] = [];

    tradesForExpirationDropdown.forEach((trade) => {
      if (trade.expiryString && trade.daysToExpiry !== undefined) {
        let isInBucket = false;

        switch (bucketType) {
          case 'ultrashort':
            isInBucket = trade.daysToExpiry <= 7;
            break;
          case 'short':
            isInBucket = trade.daysToExpiry > 7 && trade.daysToExpiry <= 35;
            break;
          case 'medium':
            isInBucket = trade.daysToExpiry > 35 && trade.daysToExpiry <= 90;
            break;
          case 'long':
            isInBucket = trade.daysToExpiry > 90;
            break;
        }

        if (isInBucket && !matchingDates.includes(trade.expiryString)) {
          matchingDates.push(trade.expiryString);
        }
      }
    });

    return matchingDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  };

  const getExpirationDatesFromBuckets = (buckets: {
    ultrashort: boolean;
    short: boolean;
    medium: boolean;
    long: boolean;
  }) => {
    const dates: string[] = [];

    if (buckets.ultrashort) {
      dates.push(...getExpirationDatesForBucket('ultrashort'));
    }
    if (buckets.short) {
      dates.push(...getExpirationDatesForBucket('short'));
    }
    if (buckets.medium) {
      dates.push(...getExpirationDatesForBucket('medium'));
    }
    if (buckets.long) {
      dates.push(...getExpirationDatesForBucket('long'));
    }

    return Array.from(new Set(dates)).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  };

  const getBucketsFromSelectedDates = (selectedDates: string[]) => {
    if (selectedDates.length === 0) {
      return { ultrashort: false, short: false, medium: false, long: false };
    }

    const buckets = {
      ultrashort: false,
      short: false,
      medium: false,
      long: false,
    };

    const ultrashortDates = getExpirationDatesForBucket('ultrashort');
    const shortDates = getExpirationDatesForBucket('short');
    const mediumDates = getExpirationDatesForBucket('medium');
    const longDates = getExpirationDatesForBucket('long');

    buckets.ultrashort = selectedDates.some((date) => ultrashortDates.includes(date));
    buckets.short = selectedDates.some((date) => shortDates.includes(date));
    buckets.medium = selectedDates.some((date) => mediumDates.includes(date));
    buckets.long = selectedDates.some((date) => longDates.includes(date));

    return buckets;
  };

  const handleTradeTypeChange = (selectedTt: string) => {
    const stateWithoutAll = state.tradeType.filter((item) => item !== 'All');
    const newItems =
      selectedTt === 'All'
        ? ['All']
        : stateWithoutAll.includes(selectedTt)
        ? stateWithoutAll.filter((item) => item !== selectedTt)
        : [...stateWithoutAll, selectedTt];

    if (newItems.length === 0) {
      newItems.push('All');
    }

    dispatch({
      type: ActionTypes.Set,
      payload: { items: { tradeType: newItems } },
    });
  };

  return (
    <Fragment>
      <div>
        <Row>
          <div className="sm:w-1/2">
            <RowTitle>Trade Side</RowTitle>
            {arePremiumSidesLocked && (
              <RowDescription>
                <span className="inline-flex flex-wrap items-center gap-1 text-yellow-400">
                  <FaLock className="h-3 w-3" />
                  Mid, Bid &amp; Below Bid are Pro.
                  <Link href={PRICING_PAGE} className="underline">
                    Upgrade to unlock
                  </Link>
                </span>
              </RowDescription>
            )}
          </div>
          <div className="flex justify-start opacity-65 hover:opacity-100 sm:justify-end">
            <FilterButtonGroupWithLabel
              activeOption={activeFlowSideLabels}
              options={FLOW_SIDE_OPTIONS}
              isOptionDisabled={(option) => isFlowSideLocked(option.value)}
              onClick={(selectedSideLabel) => {
                const selectedSide = FLOW_SIDE_OPTIONS.find(
                  (option) => option.label === selectedSideLabel,
                )?.value as FlowSideBasis | undefined;

                if (!selectedSide) {
                  return;
                }

                if (selectedSide === 'all') {
                  const nextFlowSideBasis: FlowSideBasis[] = ['all'];
                  dispatch({
                    type: ActionTypes.Set,
                    payload: { items: { flowSideBasis: nextFlowSideBasis } },
                  });
                  trackEvent(FLOW_SIDE_BASIS_CHANGED, {
                    surface: 'command_center',
                    previousFlowSideBasis: activeFlowSides.join(','),
                    nextFlowSideBasis: 'all',
                  });
                  return;
                }

                const sidesWithoutAll = activeFlowSides.filter((side) => side !== 'all');
                const newItems = sidesWithoutAll.includes(selectedSide)
                  ? sidesWithoutAll.filter((side) => side !== selectedSide)
                  : [...sidesWithoutAll, selectedSide];
                const nextFlowSideBasis: FlowSideBasis[] =
                  newItems.length > 0 ? newItems : getDefaultFlowSideBasisSelection();

                dispatch({
                  type: ActionTypes.Set,
                  payload: {
                    items: {
                      flowSideBasis: nextFlowSideBasis,
                    },
                  },
                });
                trackEvent(FLOW_SIDE_BASIS_CHANGED, {
                  surface: 'command_center',
                  previousFlowSideBasis: activeFlowSides.join(','),
                  nextFlowSideBasis: nextFlowSideBasis.join(','),
                });
              }}
            />
          </div>
        </Row>

        <Row>
          <div className="sm:w-1/2">
            <RowTitle>Mode</RowTitle>
            <RowDescription>
              Total sums all flow; Net subtracts bearish flow from bullish flow.
            </RowDescription>
          </div>
          <div className="flex justify-start opacity-65 hover:opacity-100 sm:justify-end">
            <FilterButtonGroupWithLabel
              activeOption={activeFlowMetricModeLabel}
              options={FLOW_METRIC_MODE_OPTIONS}
              onClick={(selectedModeLabel) => {
                const nextFlowMetricMode = FLOW_METRIC_MODE_OPTIONS.find(
                  (option) => option.label === selectedModeLabel,
                )?.value as FlowMetricMode | undefined;

                if (!nextFlowMetricMode) {
                  return;
                }

                dispatch({
                  type: ActionTypes.Set,
                  payload: {
                    items: {
                      flowMetricMode: nextFlowMetricMode,
                    },
                  },
                });
                trackEvent(FLOW_METRIC_MODE_CHANGED, {
                  surface: 'command_center',
                  previousMetricMode: activeFlowMetricMode,
                  nextMetricMode: nextFlowMetricMode,
                  flowSideBasis: activeFlowSides.join(','),
                });
              }}
            />
          </div>
        </Row>

        {ENABLE_OPTION_FLOW_READ_MODE && (
          <Row>
            <div className="shrink-0 sm:w-auto">
              <RowTitle>Read</RowTitle>
            </div>
            <div className="flex min-w-0 flex-1 justify-start opacity-65 hover:opacity-100 sm:justify-end [&>div]:pr-0 [&>div]:sm:flex-nowrap">
              <FilterButtonGroupWithLabel
                activeOption={activeReadLabels}
                options={READ_LABEL_OPTIONS}
                onClick={(selectedReadLabel) => {
                  if (selectedReadLabel === 'All') {
                    dispatch({
                      type: ActionTypes.Set,
                      payload: { items: { readLabels: ['All'] } },
                    });
                    return;
                  }

                  const readLabel = selectedReadLabel as FlowReadLabelSelection;
                  const labelsWithoutAll = activeReadLabels.filter((label) => label !== 'All');
                  const nextReadLabels = labelsWithoutAll.includes(readLabel)
                    ? labelsWithoutAll.filter((label) => label !== readLabel)
                    : [...labelsWithoutAll, readLabel];

                  dispatch({
                    type: ActionTypes.Set,
                    payload: {
                      items: {
                        readLabels: nextReadLabels.length > 0 ? nextReadLabels : ['All'],
                      },
                    },
                  });
                }}
              />
            </div>
          </Row>
        )}

        <Row>
          <RowTitle>Flow Type</RowTitle>
          <div className="flex w-full justify-start opacity-65 hover:opacity-100 sm:w-auto sm:justify-end">
            <FilterButtonGroupWithLabel
              activeOption={state.tradeType}
              options={TRADE_TYPE}
              className="pr-0"
              onClick={handleTradeTypeChange}
            />
          </div>
        </Row>

        <Row>
          <RowTitle>Contract Type</RowTitle>
          <div className="flex justify-start opacity-65 hover:opacity-100 sm:justify-end">
            <FilterButtonGroupWithLabel
              activeOption={activeOptionType}
              options={OPTION_TYPE}
              onClick={(selectedOt) => {
                dispatch({
                  type: ActionTypes.Set,
                  payload: { items: { optionType: selectedOt as typeof state.optionType } },
                });
              }}
            />
          </div>
        </Row>

        {!isHidden('tickerType') && (
          <Row>
            <div className="sm:w-1/2">
              <RowTitle>Ticker Type</RowTitle>
              <RowDescription>
                <div>Focus on equities for trading. ETFs are mostly hedges.</div>
              </RowDescription>
            </div>
            <div className="flex justify-start gap-y-2 opacity-65 hover:opacity-100 sm:justify-end">
              <FilterButtonGroupWithLabel
                activeOption={state.tt}
                options={TICKER_TYPE_OPTIONS}
                onClick={(selectedTt) => {
                  const newItems = TICKER_TYPE_OPTIONS.reduce((acc, option) => {
                    acc[option.label] = selectedTt === 'All' ? false : option.label === selectedTt;
                    return acc;
                  }, {} as Record<string, boolean>);
                  dispatch({
                    type: ActionTypes.Set,
                    payload: { items: { ...newItems, tt: selectedTt } },
                  });
                }}
              />
            </div>
          </Row>
        )}

        <Row enabled={enabled}>
          <div className="w-full sm:w-1/3">
            <RowTitle>Expiration</RowTitle>
            <RowDescription>
              Select any to include: {'<'} 1 week, 1 week - 1 month, 1 - 3 months, 3+ months
            </RowDescription>
          </div>
          <SliderFilter
            enabled={enabled}
            range={{ min: 0, max: 365 }}
            absoluteMin={EQUITY_FILTER.minExpiration}
            absoluteMax={EQUITY_FILTER.maxExpiration}
            unit=" Days"
            value={
              state.expirationDirection === SliderDirection.Below
                ? state.maxExpiration
                : state.minExpiration
            }
            direction={state.expirationDirection}
            onDirectionChange={(direction: SliderDirection) => {
              dispatch({
                type: ActionTypes.Set,
                payload: { items: { expirationDirection: direction } },
              });
            }}
            onChange={(min, max) => {
              dispatch({
                type: ActionTypes.Set,
                payload: {
                  items: {
                    minExpiration: min,
                    maxExpiration: max,
                    ultrashort: false,
                    short: false,
                    medium: false,
                    long: false,
                  },
                },
              });
            }}
          />
          {!enabled && (
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
              <div className="flex w-full flex-wrap gap-1 opacity-65 hover:opacity-100 sm:w-auto sm:justify-end sm:gap-0">
                <OutlineButtonWhite
                  className="flex-1 text-xs sm:flex-none sm:text-sm"
                  isActive={!state.ultrashort && !state.short && !state.medium && !state.long}
                  onClick={() => {
                    dispatch({
                      type: ActionTypes.Set,
                      payload: {
                        items: {
                          ultrashort: false,
                          short: false,
                          medium: false,
                          long: false,
                          selectedExpirations: [],
                        },
                      },
                    });
                  }}
                >
                  All
                </OutlineButtonWhite>
                <OutlineButtonWhite
                  className="flex-1 text-xs sm:flex-none sm:text-sm"
                  isActive={state.ultrashort}
                  onClick={() => {
                    const newUltraShort = !state.ultrashort;
                    const selectedDates = getExpirationDatesFromBuckets({
                      ultrashort: newUltraShort,
                      short: state.short,
                      medium: state.medium,
                      long: state.long,
                    });

                    dispatch({
                      type: ActionTypes.Set,
                      payload: {
                        items: {
                          ultrashort: newUltraShort,
                          selectedExpirations: selectedDates,
                        },
                      },
                    });
                  }}
                >
                  <span className="hidden sm:inline">Ultra Short</span>
                  <span className="sm:hidden">Ultra</span>
                </OutlineButtonWhite>
                <OutlineButtonWhite
                  className="flex-1 text-xs sm:flex-none sm:text-sm"
                  isActive={state.short}
                  onClick={() => {
                    const newShort = !state.short;
                    const selectedDates = getExpirationDatesFromBuckets({
                      ultrashort: state.ultrashort,
                      short: newShort,
                      medium: state.medium,
                      long: state.long,
                    });

                    dispatch({
                      type: ActionTypes.Set,
                      payload: {
                        items: {
                          short: newShort,
                          selectedExpirations: selectedDates,
                        },
                      },
                    });
                  }}
                >
                  Short
                </OutlineButtonWhite>
                <OutlineButtonWhite
                  className="flex-1 text-xs sm:flex-none sm:text-sm"
                  isActive={state.medium}
                  onClick={() => {
                    const newMedium = !state.medium;
                    const selectedDates = getExpirationDatesFromBuckets({
                      ultrashort: state.ultrashort,
                      short: state.short,
                      medium: newMedium,
                      long: state.long,
                    });

                    dispatch({
                      type: ActionTypes.Set,
                      payload: {
                        items: {
                          medium: newMedium,
                          selectedExpirations: selectedDates,
                        },
                      },
                    });
                  }}
                >
                  Medium
                </OutlineButtonWhite>
                <OutlineButtonWhite
                  className="flex-1 text-xs sm:flex-none sm:text-sm"
                  isActive={state.long}
                  onClick={() => {
                    const newLong = !state.long;
                    const selectedDates = getExpirationDatesFromBuckets({
                      ultrashort: state.ultrashort,
                      short: state.short,
                      medium: state.medium,
                      long: newLong,
                    });

                    dispatch({
                      type: ActionTypes.Set,
                      payload: {
                        items: {
                          long: newLong,
                          selectedExpirations: selectedDates,
                        },
                      },
                    });
                  }}
                >
                  Long
                </OutlineButtonWhite>
              </div>
              <div className="w-full sm:w-48">
                <ExpirationDateDropdown
                  trades={tradesForExpirationDropdown}
                  selectedExpirations={state.selectedExpirations || []}
                  onSelectionChange={(dates) => {
                    const buckets = getBucketsFromSelectedDates(dates);

                    dispatch({
                      type: ActionTypes.Set,
                      payload: {
                        items: {
                          selectedExpirations: dates,
                          ultrashort: buckets.ultrashort,
                          short: buckets.short,
                          medium: buckets.medium,
                          long: buckets.long,
                        },
                      },
                    });
                  }}
                />
              </div>
            </div>
          )}
        </Row>

        <Row enabled={enabled}>
          <RowTitle>Price</RowTitle>
          <SliderFilter
            enabled={enabled}
            range={{ min: 0.5, max: 50 }}
            absoluteMin={EQUITY_FILTER.minPrice}
            absoluteMax={EQUITY_FILTER.maxPrice}
            step={0.5}
            unit="$ "
            unitPosition="prefix"
            value={state.priceDirection === SliderDirection.Below ? state.maxPrice : state.minPrice}
            direction={state.priceDirection}
            onDirectionChange={(direction: SliderDirection) => {
              dispatch({
                type: ActionTypes.Set,
                payload: { items: { priceDirection: direction } },
              });
            }}
            onChange={(min, max) => {
              dispatch({
                type: ActionTypes.Set,
                payload: { items: { minPrice: min, maxPrice: max } },
              });
            }}
          />
          {!enabled && (
            <div className="w-full sm:w-auto">
              <FilterButtonGroup
                options={PRICE_OPTIONS}
                minState={state.minPrice}
                maxState={state.maxPrice}
                onClick={(min, max) => {
                  dispatch({
                    type: ActionTypes.Set,
                    payload: { items: { minPrice: min, maxPrice: max } },
                  });
                }}
              />
            </div>
          )}
        </Row>

        <Row enabled={enabled}>
          <RowTitle>Out of the Money</RowTitle>
          <SliderFilter
            enabled={enabled}
            range={{ min: -100, max: 100 }}
            unit="%"
            absoluteMin={EQUITY_FILTER.minOtm}
            absoluteMax={EQUITY_FILTER.maxOtm}
            value={state.otmDirection === SliderDirection.Below ? state.maxOtm : state.minOtm}
            direction={state.otmDirection}
            onDirectionChange={(direction: SliderDirection) => {
              dispatch({
                type: ActionTypes.Set,
                payload: { items: { otmDirection: direction } },
              });
            }}
            onChange={(min, max) => {
              dispatch({
                type: ActionTypes.Set,
                payload: { items: { minOtm: min, maxOtm: max } },
              });
            }}
          />
          {!enabled && (
            <div className="w-full sm:w-auto">
              <FilterButtonGroup
                options={OTM_OPTIONS}
                minState={state.minOtm}
                maxState={state.maxOtm}
                onClick={(min, max) => {
                  dispatch({
                    type: ActionTypes.Set,
                    payload: { items: { minOtm: min, maxOtm: max } },
                  });
                }}
              />
            </div>
          )}
        </Row>
        {!isHidden('flowDirection') && (
          <Row enabled={enabled}>
            <div className="w-full sm:w-1/3">
              <RowTitle>Put Flow %</RowTitle>
              <RowDescription>Filter by put premium as a share of total option flow</RowDescription>
            </div>
            <SliderFilter
              enabled={enabled}
              range={{ min: 0, max: 100 }}
              unit=" %"
              absoluteMin={EQUITY_FILTER.minFlow}
              absoluteMax={EQUITY_FILTER.maxFlow}
              value={state.flowDirection === SliderDirection.Below ? state.maxFlow : state.minFlow}
              direction={state.flowDirection}
              onDirectionChange={(direction: SliderDirection) => {
                dispatch({
                  type: ActionTypes.Set,
                  payload: { items: { flowDirection: direction } },
                });
              }}
              onChange={(min, max) => {
                dispatch({
                  type: ActionTypes.Set,
                  payload: { items: { minFlow: min, maxFlow: max } },
                });
              }}
            />
            {!enabled && (
              <div className="w-full sm:w-auto">
                <FilterButtonGroup
                  options={TICKER_FLOW_OPTIONS}
                  minState={state.minFlow}
                  maxState={state.maxFlow}
                  onClick={(min, max) => {
                    dispatch({
                      type: ActionTypes.Set,
                      payload: { items: { minFlow: min, maxFlow: max } },
                    });
                  }}
                />
              </div>
            )}
          </Row>
        )}

        {!isHidden('momentum') && (
          <Row enabled={enabled}>
            <div className="w-full sm:w-1/2">
              <RowTitle>Momentum</RowTitle>
              <RowDescription>
                Tickers with large volume relative to their 5-day average
              </RowDescription>
            </div>
            <SliderFilter
              enabled={enabled}
              range={{ min: 1, max: 10 }}
              unit="x"
              absoluteMin={EQUITY_FILTER.minMomentum}
              absoluteMax={EQUITY_FILTER.maxMomentum}
              step={0.5}
              value={
                state.momentumDirection === SliderDirection.Below
                  ? state.maxMomentum
                  : state.minMomentum
              }
              direction={state.momentumDirection}
              onDirectionChange={(direction: SliderDirection) => {
                dispatch({
                  type: ActionTypes.Set,
                  payload: { items: { momentumDirection: direction } },
                });
              }}
              onChange={(min, max) => {
                dispatch({
                  type: ActionTypes.Set,
                  payload: { items: { minMomentum: min, maxMomentum: max } },
                });
              }}
            />
            {!enabled && (
              <div className="w-full sm:w-auto">
                <FilterButtonGroup
                  options={MOMENTUM_OPTIONS}
                  minState={state.minMomentum}
                  maxState={state.maxMomentum}
                  onClick={(min, max) => {
                    dispatch({
                      type: ActionTypes.Set,
                      payload: { items: { minMomentum: min, maxMomentum: max } },
                    });
                  }}
                />
              </div>
            )}
          </Row>
        )}

        <Row enabled={enabled}>
          <RowTitle>Size</RowTitle>
          <SliderFilter
            enabled={enabled}
            range={{ min: 0, max: 10000 }}
            absoluteMin={EQUITY_FILTER.minSize}
            absoluteMax={EQUITY_FILTER.maxSize}
            unit="K"
            step={500}
            value={state.sizeDirection === SliderDirection.Below ? state.maxSize : state.minSize}
            direction={state.sizeDirection}
            onDirectionChange={(direction: SliderDirection) => {
              dispatch({
                type: ActionTypes.Set,
                payload: { items: { sizeDirection: direction } },
              });
            }}
            onChange={(min, max) => {
              dispatch({
                type: ActionTypes.Set,
                payload: { items: { minSize: min, maxSize: max } },
              });
            }}
          />
          {!enabled && (
            <div className="w-full sm:w-auto">
              <FilterButtonGroup
                options={SIZE_OPTIONS}
                minState={state.minSize}
                maxState={state.maxSize}
                onClick={(min, max) => {
                  dispatch({
                    type: ActionTypes.Set,
                    payload: { items: { minSize: min, maxSize: max } },
                  });
                }}
              />
            </div>
          )}
        </Row>

        <Row enabled={enabled}>
          <RowTitle>Premium</RowTitle>
          <SliderFilter
            enabled={enabled}
            range={{ min: 0, max: 1000000 }}
            unit="K"
            step={5000}
            absoluteMin={EQUITY_FILTER.minPremium}
            absoluteMax={EQUITY_FILTER.maxPremium}
            value={
              state.premiumDirection === SliderDirection.Below ? state.maxPremium : state.minPremium
            }
            direction={state.premiumDirection}
            onDirectionChange={(direction: SliderDirection) => {
              dispatch({
                type: ActionTypes.Set,
                payload: { items: { premiumDirection: direction } },
              });
            }}
            onChange={(min, max) => {
              dispatch({
                type: ActionTypes.Set,
                payload: { items: { minPremium: min, maxPremium: max } },
              });
            }}
          />
          {!enabled && (
            <div className="w-full sm:w-auto">
              <FilterButtonGroup
                options={PREMIUM_OPTIONS}
                minState={state.minPremium}
                maxState={state.maxPremium}
                onClick={(min, max) => {
                  dispatch({
                    type: ActionTypes.Set,
                    payload: { items: { minPremium: min, maxPremium: max } },
                  });
                }}
              />
            </div>
          )}
        </Row>

        <Row enabled={enabled}>
          <RowTitle>Implied Volatility</RowTitle>
          <SliderFilter
            enabled={enabled}
            range={{ min: 0, max: 100 }}
            unit=" %"
            absoluteMin={EQUITY_FILTER.minImpVolatility}
            absoluteMax={EQUITY_FILTER.maxImpVolatility}
            value={
              state.ivDirection === SliderDirection.Below
                ? state.maxImpVolatility
                : state.minImpVolatility
            }
            direction={state.ivDirection}
            onDirectionChange={(direction: SliderDirection) => {
              dispatch({
                type: ActionTypes.Set,
                payload: { items: { ivDirection: direction } },
              });
            }}
            onChange={(min, max) => {
              dispatch({
                type: ActionTypes.Set,
                payload: { items: { minImpVolatility: min, maxImpVolatility: max } },
              });
            }}
          />
          {!enabled && (
            <div className="w-full sm:w-auto">
              <FilterButtonGroup
                options={VOLATILITY_OPTIONS}
                minState={state.minImpVolatility}
                maxState={state.maxImpVolatility}
                onClick={(min, max) => {
                  dispatch({
                    type: ActionTypes.Set,
                    payload: { items: { minImpVolatility: min, maxImpVolatility: max } },
                  });
                }}
              />
            </div>
          )}
        </Row>

        <Row enabled={enabled}>
          <RowTitle>Open Interest</RowTitle>
          <SliderFilter
            enabled={enabled}
            range={{ min: 0, max: 75000 }}
            unit="K"
            step={1000}
            absoluteMin={EQUITY_FILTER.minOpenInterest}
            absoluteMax={EQUITY_FILTER.maxOpenInterest}
            value={
              state.interestDirection === SliderDirection.Below
                ? state.maxOpenInterest
                : state.minOpenInterest
            }
            direction={state.interestDirection}
            onDirectionChange={(direction: SliderDirection) => {
              dispatch({
                type: ActionTypes.Set,
                payload: { items: { interestDirection: direction } },
              });
            }}
            onChange={(min, max) => {
              dispatch({
                type: ActionTypes.Set,
                payload: { items: { minOpenInterest: min, maxOpenInterest: max } },
              });
            }}
          />
          {!enabled && (
            <div className="w-full sm:w-auto">
              <FilterButtonGroup
                options={OPEN_INTEREST_OPTIONS}
                minState={state.minOpenInterest}
                maxState={state.maxOpenInterest}
                onClick={(min, max) => {
                  dispatch({
                    type: ActionTypes.Set,
                    payload: { items: { minOpenInterest: min, maxOpenInterest: max } },
                  });
                }}
              />
            </div>
          )}
        </Row>

        {!isHidden('marketCap') && (
          <Row enabled={enabled}>
            <div className="w-full sm:w-1/4">
              <RowTitle>Market Cap</RowTitle>
            </div>
            <SliderFilter
              enabled={enabled}
              range={{ min: 0, max: 100_000_000_000 }}
              unit="M"
              step={10_000_000}
              absoluteMin={EQUITY_FILTER.minMarketCap}
              absoluteMax={EQUITY_FILTER.maxMarketCap}
              value={
                state.marketCapDirection === SliderDirection.Below
                  ? state.maxMarketCap
                  : state.minMarketCap
              }
              direction={state.marketCapDirection}
              onDirectionChange={(direction: SliderDirection) => {
                dispatch({
                  type: ActionTypes.Set,
                  payload: { items: { marketCapDirection: direction } },
                });
              }}
              onChange={(min, max) => {
                dispatch({
                  type: ActionTypes.Set,
                  payload: { items: { minMarketCap: min, maxMarketCap: max } },
                });
              }}
            />
            {!enabled && (
              <div className="w-full sm:w-auto">
                <FilterButtonGroup
                  options={MARKET_CAP_OPTIONS}
                  minState={state.minMarketCap}
                  maxState={state.maxMarketCap}
                  onClick={(min, max) => {
                    dispatch({
                      type: ActionTypes.Set,
                      payload: { items: { minMarketCap: min, maxMarketCap: max } },
                    });
                  }}
                />
              </div>
            )}
          </Row>
        )}

        {!isHidden('sector') && (
          <Row>
            <RowTitle>Sector</RowTitle>
            <div className="flex w-full flex-wrap justify-start gap-y-2 opacity-65 hover:opacity-100 sm:w-[70%] sm:justify-end">
              <FilterButtonGroupWithLabel
                activeOption={state.activeSector}
                options={SECTOR_OPTIONS}
                onClick={(selectedSector) => {
                  const stateWithoutAll = state.activeSector.filter((item) => item !== 'All');
                  const newItems =
                    selectedSector === 'All'
                      ? ['All']
                      : stateWithoutAll.includes(selectedSector)
                      ? stateWithoutAll.filter((item) => item !== selectedSector)
                      : [...stateWithoutAll, selectedSector];

                  if (newItems.length === 0) {
                    newItems.push('All');
                  }

                  dispatch({
                    type: ActionTypes.Set,
                    payload: { items: { activeSector: newItems } },
                  });
                }}
              />
            </div>
          </Row>
        )}
        <Row enabled={enabled}>
          <div>
            <RowTitle>Time</RowTitle>
          </div>
          {enabled && (
            <SliderFilter
              enabled={enabled}
              range={{ min: 0, max: 240 }}
              absoluteMin={EQUITY_FILTER.minTime}
              absoluteMax={EQUITY_FILTER.maxTime}
              unit={' Min'}
              step={15}
              value={state.timeDirection === SliderDirection.Below ? state.maxTime : state.minTime}
              direction={state.timeDirection}
              onDirectionChange={(direction: SliderDirection) => {
                dispatch({
                  type: ActionTypes.Set,
                  payload: { items: { timeDirection: direction } },
                });
              }}
              onChange={(min, max) => {
                dispatch({
                  type: ActionTypes.Set,
                  payload: { items: { minTime: min, maxTime: max } },
                });
              }}
            />
          )}
          {!enabled && (
            <div className="w-full gap-1 sm:w-auto">
              <FilterButtonGroup
                options={TIME_OPTIONS}
                minState={state.minTime}
                maxState={state.maxTime}
                onClick={(min, max) => {
                  dispatch({
                    type: ActionTypes.Set,
                    payload: { items: { minTime: min, maxTime: max } },
                  });
                }}
              />
            </div>
          )}
        </Row>
      </div>
    </Fragment>
  );
}
